/**
 * FileMenu.jsx — File top-level menu.
 *
 * Covers: New Project, Save, Save As, Publish, Bulk Wizard, Studio
 * Dashboard, Royalties, ISBN, Audiobook, Premier Studio section,
 * Export sub-menu, Preferences, and Exit.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuShortcut,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Save, FileText, BookOpen as BookOpenIcon, Coins, Library, Wallet, Hash,
  Headphones, Users, ScrollText, Palette, UserCog, Zap, Factory, Download,
  Edit3, FolderOpen, Grid3x3, Mic, FileArchive, ShieldAlert, Drama,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import { useAuth } from '@/lib/auth_context';
import { exportExtrasApi } from '@/lib/api';
import { Menu, MenuItem, GatedMenuItem } from './shared';
import { openStudioDashboard } from '@/components/StudioProductionDashboard';
import { GLOW_TRIGGER_CLASS, GLOW_ITEM_CLASS } from '@/components/OnboardingFileMenuGlow';

export function FileMenu({
  onNewProject, onSaveAs, onPublishToBookstore,
  onMyRoyalties, onIsbnManager, onAudiobook, onNarrationStudio,
  onBookCoaching, onRequestEditorReview, onCanvaCover,
  onAccountConnections, onOpenStacks, onOpenFunds,
  onExport, onSettings, simpleMode,
  onOpenBulk, onOpenNovelFactory, onExitRequest, onImportManuscript,
  onScriptConverter, onKdpMetadata,
  // Onboarding glow props
  glowTrigger, glowLoadManuscript, onFileMenuOpen, onLoadManuscriptClicked,
}) {
  const { t } = useTranslation();
  const { project, activeChapterId } = usePW();
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin || user?.role === 'admin' || user?.role === 'super_admin');

  const downloadExtra = (kind, fmt, chapterId) => {
    if (!project) { toast.error('No project loaded'); return; }
    const url = kind === 'characters'
      ? exportExtrasApi.charactersUrl(project.id, fmt)
      : exportExtrasApi.variablesUrl(project.id, fmt, chapterId);
    const a = document.createElement('a');
    a.href = url; a.target = '_blank'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Menu
      label={t('menu.file')}
      triggerClassName={glowTrigger ? GLOW_TRIGGER_CLASS : ''}
      onOpen={onFileMenuOpen}
    >
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t('menu.project')}
      </DropdownMenuLabel>
      <MenuItem onClick={onNewProject} data-testid="menu-new-project"
        tip="Create a blank manuscript to begin writing your new novel">
        <FileText className="h-3.5 w-3.5" /> {t('menu.newProject')}
        <DropdownMenuShortcut>Ctrl+N</DropdownMenuShortcut>
      </MenuItem>
      <MenuItem onClick={() => toast.success(t('common.saved'))} data-testid="menu-save"
        tip="Save your current chapter — Ctrl+S works too">
        <Save className="h-3.5 w-3.5" /> {t('menu.save')}
        <DropdownMenuShortcut>Ctrl+S</DropdownMenuShortcut>
      </MenuItem>
      <MenuItem onClick={onSaveAs} data-testid="menu-save-as"
        tip="Save a copy of this manuscript under a new title">
        <Save className="h-3.5 w-3.5" /> {t('menu.saveAs')}
        <DropdownMenuShortcut>Ctrl+Shift+S</DropdownMenuShortcut>
      </MenuItem>
      <MenuItem
        onClick={() => { onImportManuscript?.(); onLoadManuscriptClicked?.(); }}
        data-testid="menu-file-load-manuscript"
        tip="Import a Word (.docx) or text file to start working on your draft"
        className={`text-[12.5px] gap-2 cursor-pointer ${glowLoadManuscript ? GLOW_ITEM_CLASS : ''}`}
      >
        <FolderOpen className="h-3.5 w-3.5" /> Load Manuscript…
        <DropdownMenuShortcut>Ctrl+O</DropdownMenuShortcut>
      </MenuItem>
      <MenuItem
        onClick={() => window.dispatchEvent(new CustomEvent('loom:open-bulk-manuscripts'))}
        data-testid="menu-bulk-manuscript-tools"
        tip="Download all your manuscripts as a ZIP, or import a batch at once"
      >
        <FileArchive className="h-3.5 w-3.5" /> Bulk Export / Import…
      </MenuItem>
      {isAdmin && (
        <MenuItem
          onClick={() => window.dispatchEvent(new CustomEvent('loom:open-admin-project-picker'))}
          data-testid="menu-open-admin-project"
        >
          <ShieldAlert className="h-3.5 w-3.5 text-amber-500" /> Open Admin Project…
        </MenuItem>
      )}
      {project && (
        <MenuItem
          onClick={() => window.dispatchEvent(new CustomEvent('loom:open-manuscript-rename'))}
          data-testid="menu-rename-manuscript"
        >
          <Edit3 className="h-3.5 w-3.5" /> Rename Manuscript…
        </MenuItem>
      )}
      <DropdownMenuSeparator />
      <GatedMenuItem capability="publish_to_bookstore" onClick={onPublishToBookstore} data-testid="menu-publish-bookstore"
        tip="List your completed novel on the Crown & Rose reader store">
        <BookOpenIcon className="h-3.5 w-3.5" /> {t('menu.publishToBookstore')}
      </GatedMenuItem>
      {!simpleMode && (
        <GatedMenuItem capability="bulk_draft_novel" onClick={onOpenBulk} data-testid="menu-bulk-novel-wizard"
          tip="AI-draft and publish multiple novels in an automated batch">
          <Zap className="h-3.5 w-3.5 text-oxblood-600" /> {t('menu.bulkNovelWizard')}
        </GatedMenuItem>
      )}
      {!simpleMode && (
        <GatedMenuItem capability="bulk_draft_novel" onClick={openStudioDashboard} data-testid="menu-studio-dashboard"
          tip="Track the progress of all your AI-drafted novels across the pipeline">
          <Factory className="h-3.5 w-3.5 text-oxblood-600" /> {t('menu.studioDashboard')}
        </GatedMenuItem>
      )}
      {!simpleMode && onOpenNovelFactory && (
        <MenuItem onClick={onOpenNovelFactory} data-testid="menu-novel-factory">
          <Zap className="h-3.5 w-3.5 text-[#c0394a]" /> Novel Factory
          <DropdownMenuShortcut className="text-[10px] text-white/30">Admin</DropdownMenuShortcut>
        </MenuItem>
      )}
      <GatedMenuItem capability="royalties_dashboard" onClick={onMyRoyalties} data-testid="menu-my-royalties"
        tip="View your sales reports and monthly royalty statements">
        <Coins className="h-3.5 w-3.5" /> {t('menu.myRoyalties')}
      </GatedMenuItem>
      <GatedMenuItem capability="isbn_manager" onClick={onIsbnManager} data-testid="menu-isbn-manager"
        tip="Request and assign ISBNs for your published titles">
        <Hash className="h-3.5 w-3.5" /> {t('menu.isbnManager')}
      </GatedMenuItem>
      <GatedMenuItem capability="audiobook_generation" onClick={onAudiobook} data-testid="menu-audiobook"
        tip="Convert your manuscript into a narrated MP3 audiobook">
        <Headphones className="h-3.5 w-3.5" /> {t('menu.generateAudiobook')}
      </GatedMenuItem>
      <MenuItem onClick={onNarrationStudio} data-testid="menu-narration-studio"
        tip="Record or AI-generate chapter-by-chapter audio narration">
        <Mic className="h-3.5 w-3.5" /> Narration Studio
      </MenuItem>
      <GatedMenuItem capability="convert_to_script" onClick={onScriptConverter} data-testid="menu-convert-to-script"
        tip="Turn your novel into a screenplay, stage play, or teleplay">
        <Drama className="h-3.5 w-3.5" /> Convert to Script…
      </GatedMenuItem>
      <GatedMenuItem capability="kdp_metadata" onClick={onKdpMetadata} data-testid="menu-kdp-metadata"
        tip="Fill in Amazon's required publishing details for Kindle Direct Publishing">
        <FileText className="h-3.5 w-3.5" /> KDP Metadata…
      </GatedMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t('menu.premierStudio')}
      </DropdownMenuLabel>
      <GatedMenuItem capability="writer_coaching" onClick={onBookCoaching} data-testid="menu-book-coaching"
        tip="Book a one-on-one session with a Crown & Rose editorial coach">
        <Users className="h-3.5 w-3.5" /> {t('menu.bookCoaching')}
      </GatedMenuItem>
      <GatedMenuItem capability="monthly_editor_review" onClick={onRequestEditorReview} data-testid="menu-request-editor-review"
        tip="Submit your manuscript to a Crown & Rose human editor for review">
        <ScrollText className="h-3.5 w-3.5" /> {t('menu.requestEditorReview')}
      </GatedMenuItem>
      <MenuItem onClick={onCanvaCover} data-testid="menu-canva-cover"
        tip="Open the Canva cover designer with your book details pre-loaded">
        <Palette className="h-3.5 w-3.5" /> {t('menu.designCoverCanva')}
      </MenuItem>
      <MenuItem onClick={onAccountConnections} data-testid="menu-account-connections"
        tip="Link your Crown & Rose account to external services">
        <UserCog className="h-3.5 w-3.5" /> {t('menu.accountConnections')}
      </MenuItem>
      <MenuItem onClick={onOpenStacks} data-testid="menu-stacks"
        tip="Browse and search all manuscripts across your publishing library">
        <Library className="h-3.5 w-3.5" /> {t('menu.theStacks')}
      </MenuItem>
      <MenuItem onClick={onOpenFunds} data-testid="menu-funds"
        tip="View your account balance and transaction history">
        <Wallet className="h-3.5 w-3.5" /> {t('menu.myFunds')}
      </MenuItem>
      <DropdownMenuSeparator />
      {/* Export sub-menu */}
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="text-[12.5px]" data-testid="menu-export-trigger">
          {t('menu.exportTrigger')}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            {t('menu.manuscript')}
          </DropdownMenuLabel>
          <MenuItem onClick={onExport} data-testid="menu-export-novel">{t('menu.exportNovel')}</MenuItem>
          <MenuItem onClick={onPublishToBookstore} data-testid="menu-export-bookstore">
            <BookOpenIcon className="h-3.5 w-3.5" /> {t('menu.publishBookstore')}
          </MenuItem>
          <MenuItem
            onClick={async () => {
              const pid = project?.id;
              if (!pid) return;
              try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/${pid}/book-template`, {
                  method: 'POST', credentials: 'include',
                });
                if (!res.ok) throw new Error((await res.json())?.detail || 'Export failed');
                const blob = await res.blob();
                const cd = res.headers.get('content-disposition') || '';
                const m = /filename="([^"]+)"/.exec(cd);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = m ? m[1] : 'book-template.docx';
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                // eslint-disable-next-line no-alert
                alert(err.message || 'Could not save book template.');
              }
            }}
            data-testid="menu-export-book-template"
          >
            <FileText className="h-3.5 w-3.5" /> {t('menu.saveAsBookTemplate')}
          </MenuItem>
          <MenuItem
            onClick={async () => {
              const pid = project?.id;
              if (!pid) return;
              toast.info('Distilling your rolling summary into a reusable framework…');
              try {
                const base = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/export-as-framework`;
                const start = await fetch(base, { method: 'POST', credentials: 'include' });
                const startData = await start.json();
                if (!start.ok) throw new Error(startData?.detail || 'Export failed');
                const jobId = startData.id;
                for (let i = 0; i < 80; i++) {
                  await new Promise((r) => setTimeout(r, 2500));
                  const poll = await fetch(`${base}/${jobId}`, { credentials: 'include' });
                  const job = await poll.json();
                  if (!poll.ok) throw new Error(job?.detail || 'Export failed');
                  if (job.status === 'done') {
                    toast.success('Synopsis Framework created — find it in Build Novel Framework…', { duration: 5000 });
                    return;
                  }
                  if (job.status === 'error' || job.status === 'stalled') throw new Error(job.error || 'Export failed');
                }
                toast.error('Still working — check Build Novel Framework… shortly.');
              } catch (err) {
                toast.error(err.message || 'Could not export synopsis framework.');
              }
            }}
            data-testid="menu-export-synopsis-framework"
          >
            <ScrollText className="h-3.5 w-3.5" /> {t('menu.exportSynopsisFramework')}
          </MenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            {t('menu.charBibleLabel')}
          </DropdownMenuLabel>
          <MenuItem onClick={() => downloadExtra('characters', 'pdf')} data-testid="menu-export-chars-pdf">
            <Users className="h-3.5 w-3.5" /> {t('menu.exportCharsPdf')}
          </MenuItem>
          <MenuItem onClick={() => downloadExtra('characters', 'docx')} data-testid="menu-export-chars-docx">
            <Users className="h-3.5 w-3.5" /> {t('menu.exportCharsDocx')}
          </MenuItem>
          <MenuItem onClick={() => downloadExtra('characters', 'txt')} data-testid="menu-export-chars-txt">
            <Users className="h-3.5 w-3.5" /> {t('menu.exportCharsTxt')}
          </MenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            {t('menu.variableGridLabel')}
          </DropdownMenuLabel>
          <MenuItem onClick={() => downloadExtra('variables', 'pdf')} data-testid="menu-export-vars-pdf-full">
            <Grid3x3 className="h-3.5 w-3.5" /> {t('menu.exportVarsFullPdf')}
          </MenuItem>
          <MenuItem onClick={() => downloadExtra('variables', 'txt')} data-testid="menu-export-vars-csv-full">
            <Grid3x3 className="h-3.5 w-3.5" /> {t('menu.exportVarsFullCsv')}
          </MenuItem>
          <MenuItem
            onClick={() => downloadExtra('variables', 'pdf', activeChapterId)}
            disabled={!activeChapterId}
            data-testid="menu-export-vars-pdf-current"
          >
            <Grid3x3 className="h-3.5 w-3.5" /> {t('menu.exportVarsCurrentPdf')}
          </MenuItem>
          <MenuItem
            onClick={() => downloadExtra('variables', 'txt', activeChapterId)}
            disabled={!activeChapterId}
            data-testid="menu-export-vars-csv-current"
          >
            <Grid3x3 className="h-3.5 w-3.5" /> {t('menu.exportVarsCurrentCsv')}
          </MenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      {/* Self-serve ZIP — download all personal manuscripts in one click */}
      <MenuItem
        onClick={async () => {
          toast.info('Building your manuscript archive — this may take a moment…');
          try {
            const base = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(`${base}/api/projects/my/export-zip`, { credentials: 'include' });
            if (!res.ok) throw new Error((await res.json())?.detail || 'Export failed');
            const blob = await res.blob();
            const cd = res.headers.get('content-disposition') || '';
            const m = /filename="([^"]+)"/.exec(cd);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = m ? m[1] : 'my_manuscripts.zip';
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            const count = res.headers.get('x-project-count') || '?';
            toast.success(`Downloaded ${count} manuscript${count === '1' ? '' : 's'} as ZIP.`);
          } catch (err) {
            toast.error(err.message || 'Could not build ZIP archive.');
          }
        }}
        data-testid="menu-export-all-zip"
        tip="Export every manuscript you own as a single .zip archive"
      >
        <FileArchive className="h-3.5 w-3.5" /> Download All Manuscripts (ZIP)
      </MenuItem>

      <DropdownMenuSeparator />
      <MenuItem onClick={onSettings}
        tip="Adjust your writing environment, AI keys, subscriptions, and account settings">
        {t('menu.preferences')}<DropdownMenuShortcut>Ctrl+,</DropdownMenuShortcut>
      </MenuItem>
      <MenuItem
        className="text-destructive"
        onClick={onExitRequest}
        data-testid="menu-exit"
      >
        {t('menu.exit')}<DropdownMenuShortcut>Alt+F4</DropdownMenuShortcut>
      </MenuItem>
    </Menu>
  );
}
